/**
 * Prize rules and payouts — D16, and the part of the model that would be easiest to get wrong in
 * a way that costs a community real money.
 *
 * The pilot's answer looked like a simple scale: youth 0–7 $40 daily, 8–12 $60, 13–17 $80,
 * adults in regalia $100. Note what that actually is — a **flat daily amount per participant by
 * age group**, not a placement prize. Paying every youth dancer who dances is a different
 * mechanism from paying first, second and third, and the published posters show both running in
 * the same event alongside a $5000 exhibition pool. A model that supported only placement would
 * fail this one event three ways over.
 *
 * So a rule answers four questions, and keeping them independent is the entire point:
 *
 * | Field         | Answers        |
 * | ------------- | -------------- |
 * | `scopeType`   | who it applies to |
 * | `basis`       | how much       |
 * | `eligibility` | who qualifies  |
 * | `cadence`     | how often      |
 *
 * Fusing "how much" with "who qualifies" would need a new enum member for every combination —
 * and the next community would need a code change to be paid the way it has always paid.
 */

import { index, integer, sqliteTable, text } from "drizzle-orm/sqlite-core";
import { archivable, audited, cents, currency, flag, id, instant, ref } from "./columns.js";

/**
 * Scoping to a **dimension value** rather than a category is what lets one row cover both the
 * boys' and the girls' category of an age band. The payout is coarser than the category, and the
 * model allows that.
 */
export const PRIZE_SCOPES = [
  "event",
  "competition",
  "category",
  "dimension_value",
  "role",
] as const;

/**
 * How much.
 *
 * `agreed` takes the amount from the role assignment rather than from the rule (D63). It exists
 * because the MC's fee is negotiated with the MC — one rule, many amounts — where an elders'
 * honorarium is a community rate that applies to whoever appears. Both are now expressible, and
 * which a community uses is its answer rather than ours.
 */
export const PRIZE_BASES = ["participation", "placement", "pooled", "agreed"] as const;

/**
 * Who qualifies — orthogonal to basis, and every combination is meaningful. A flat honorarium to
 * everyone who danced is `participation` + `all`. The same amount paid only to those who did
 * *not* place — the familiar consolation or gas-money practice — is `participation` +
 * `unplaced`. A top-up for winners is `participation` + `placed`.
 */
export const PRIZE_ELIGIBILITIES = ["all", "placed", "unplaced", "outside_top_n"] as const;

/** A daily honorarium and a once-per-weekend placement prize attach to the same category. */
/** How often a rule pays: each session, each day of the event, or once for the whole thing (D74). */
export const PRIZE_CADENCES = ["per_session", "per_day", "per_event"] as const;

export const POOL_SPLITS = ["equal", "by_placement"] as const;

export const prizeRules = sqliteTable(
  "prize_rules",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    eventId: ref("event_id").notNull(),
    name: text("name").notNull(),

    basis: text("basis", { enum: PRIZE_BASES }).notNull(),
    eligibility: text("eligibility", { enum: PRIZE_ELIGIBILITIES }).notNull().default("all"),
    /** The cutoff for `outside_top_n`. Stored on the rule, so "everyone below 5th" is data. */
    eligibilityRankCutoff: integer("eligibility_rank_cutoff"),

    /** `participation`: the flat amount per qualifying entry. */
    amountCents: cents("amount_cents"),
    /** `pooled`: the fixed total divided across qualifying entries. */
    poolTotalCents: cents("pool_total_cents"),
    poolSplit: text("pool_split", { enum: POOL_SPLITS }),

    cadence: text("cadence", { enum: PRIZE_CADENCES }).notNull().default("per_event"),
    currency: currency(),

    /**
     * Check-in is the ground truth for participation: registering and not dancing earns nothing.
     * This defaults on, because paying against the entry list rather than attendance is the
     * mistake that is expensive and invisible.
     */
    requiresCheckin: flag("requires_checkin").notNull().default(true),

    active: flag("active").notNull().default(true),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("prize_rules_event_idx").on(table.eventId, table.active)],
);

/**
 * What a prize rule applies to (D52). A rule scopes to a *set* of these; an empty set is everyone in
 * the event. Each row is one target, and the three ids compose so the hierarchy an organiser means
 * is expressible:
 *
 * - `competitionId` alone — a whole competition ("everyone in Drum Roll").
 * - `valueId` alone — a band across every competition ("Juniors, wherever they dance").
 * - `competitionId` **and** `valueId` — a band within one competition ("Juniors under Dance").
 * - `categoryId` alone — one exact category.
 * - `eventRoleId` alone — everyone holding a role ("the elders", "the invited drums").
 *
 * The union of the rule's rows decides which entries it pays, so "Juniors and Regular under Dance,
 * and everyone in Drum Roll" is three rows on one rule.
 *
 * A role target matches role holders **only**, and the other three match competitors only (D63).
 * Both halves matter: without the second, a rule with no targets — which means the whole event —
 * would quietly pay the MC a dancer's honorarium.
 */
export const prizeRuleScopes = sqliteTable(
  "prize_rule_scopes",
  {
    id: id(),
    prizeRuleId: ref("prize_rule_id").notNull(),
    communityId: ref("community_id").notNull(),
    competitionId: ref("competition_id"),
    categoryId: ref("category_id"),
    valueId: ref("value_id"),
    /** Everyone holding this role (D62, D63) — the elders, the flag carriers, the arena director. */
    eventRoleId: ref("event_role_id"),
    ...audited(),
  },
  (table) => [index("prize_rule_scopes_rule_idx").on(table.prizeRuleId)],
);

/** `placement`: an amount per place. 1st–5th for dance, 1st–4th for the specials. */
export const prizeRulePlacements = sqliteTable(
  "prize_rule_placements",
  {
    id: id(),
    prizeRuleId: ref("prize_rule_id").notNull(),
    communityId: ref("community_id").notNull(),
    place: integer("place").notNull(),
    amountCents: cents("amount_cents").notNull(),
    ...audited(),
  },
  (table) => [index("prize_rule_placements_rule_idx").on(table.prizeRuleId, table.place)],
);

export const PAYOUT_STATUSES = ["calculated", "approved", "paid", "void"] as const;

/**
 * One line of money owed to one person.
 *
 * Eligibility is evaluated **per entry**, not per person: a dancer who places in one category and
 * not another collects the prize for the first and the consolation for the second. That is the
 * behaviour people expect and the only one that survives contact with a multi-category dancer.
 *
 * Recalculation produces a new `calculated` row and voids the superseded one with a reason. A row
 * marked `paid` is never mutated, because it corresponds to cash that has already left the table;
 * a correction is a void plus a new line.
 *
 * `explanationJson` holds the plain-language reason this line exists — *Youth 8–12 honorarium
 * (everyone who danced)*, *1st place, Adults Men's Traditional*, *Exhibition pool, 1 of 34
 * shares*. A treasurer must be able to explain any number to the person receiving it (ux.md §3.9).
 *
 * One table for dancers and for everyone else (D63). That is what lets the treasurer's sheet, the
 * per-session total and the event's committed cost span both without a second code path — which is
 * why paying role holders was folded into D16 rather than built beside it.
 */
export const payouts = sqliteTable(
  "payouts",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    eventId: ref("event_id").notNull(),
    sessionId: ref("session_id"),
    prizeRuleId: ref("prize_rule_id").notNull(),

    /**
     * The payee: a person, **or** a team. Nullable participant because a host drum honorarium is
     * paid to the drum rather than to its captain, and `teams.distributionNote` already records how
     * they split it. Exactly one of the two is set.
     */
    participantId: ref("participant_id"),
    teamId: ref("team_id"),

    /** What earned it: a competitor's entry, or a role holder's assignment (D62). One of the two. */
    registrationEntryId: ref("registration_entry_id"),
    roleAssignmentId: ref("role_assignment_id"),
    resultId: ref("result_id"),

    amountCents: cents("amount_cents").notNull(),
    currency: currency(),
    status: text("status", { enum: PAYOUT_STATUSES }).notNull().default("calculated"),

    explanationJson: text("explanation_json", { mode: "json" }),

    calculatedAt: instant("calculated_at").notNull(),
    approvedBy: ref("approved_by"),
    approvedAt: instant("approved_at"),
    paidAt: instant("paid_at"),
    paidBy: ref("paid_by"),
    paidMethod: text("paid_method"),
    reference: text("reference"),
    voidReason: text("void_reason"),
    voidedBy: ref("voided_by"),
    voidedAt: instant("voided_at"),
    supersededByPayoutId: ref("superseded_by_payout_id"),
    ...audited(),
  },
  (table) => [
    index("payouts_event_session_idx").on(table.eventId, table.sessionId, table.status),
    index("payouts_participant_idx").on(table.participantId),
    index("payouts_team_idx").on(table.teamId),
    index("payouts_role_assignment_idx").on(table.roleAssignmentId),
    index("payouts_rule_idx").on(table.prizeRuleId),
  ],
);
